import { Avatar, Box, IconButton, Radio, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import { BpmBox, DateText, LabelDateBox } from "./styles";
import { LabelText, TimeBox, VersionDetailsBox, styles } from "./styles";
import { TrackVersionsProps, month } from "./types";
import { useContext, useEffect, useState } from "react";
import { useGetUser } from "../../hooks/useGetUser";
import { User } from "../../proto/api_pb";
import { NavLink } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import { AuthContext } from "../../contexts";
import { LightTooltip } from "../LightToolTip/LightToolTip";

export const TrackVersions: React.FC<TrackVersionsProps> = ({
  stopAllSongs,
  handlePlayPause,
  version,
  isPlaying,
  id,
  bpm,
  label,
  timeStamp,
  ownerId,
  isSelected,
  selectedVersionId,
  handleChange,
  updateSelectedTrackVersion,
  handleOpenDeleteModal,
}) => {
  const { user } = useGetUser(ownerId);
  const [audio] = useState(new Audio(version));
  const [owner, setOwner] = useState<User>();
  const [checked, setChecked] = useState<boolean>(false);
  const { userId } = useContext(AuthContext);
  const date = new Date(timeStamp * 1000);
  const year = date.getFullYear();
  const mm = month[date.getMonth()];
  const dd = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const switchHandleChange = () => {
    setChecked(!checked);
    handleChange(timeStamp);
    updateSelectedTrackVersion();
  };

  const downloadSong = async () => {
    try {
      const response = await fetch(version);
      const songBlob = await response.blob();
      const songUrl = URL.createObjectURL(songBlob);
      const link = document.createElement("a");
      link.href = songUrl;
      link.download = label!;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log("Download Error");
      console.log(error);
    }
  };

  useEffect(() => {
    if (audio && audio.paused) audio.src = version;
  }, [audio, version]);

  useEffect(() => {
    if (selectedVersionId === id) setChecked(isSelected);
    setOwner(user);
    if (audio) {
      isPlaying ? audio.play() : audio.pause();
    }
  }, [audio, isPlaying, user, id, selectedVersionId, isSelected]);

  useEffect(() => {
    audio && audio.addEventListener("ended", () => stopAllSongs());

    return () => {
      audio && audio.removeEventListener("ended", () => stopAllSongs());
    };
  }, [audio, id, stopAllSongs]);

  useEffect(() => {
    return () => {
      audio.pause();
    };
  }, [audio, user]);

  return (
    <>
      <VersionDetailsBox>
        <Box>
          <Tooltip title={`Set "${label}" as current track version`}>
            <Radio
              checked={isSelected}
              onChange={switchHandleChange}
              name="radio-buttons"
              inputProps={{ "aria-label": "A" }}
            />
          </Tooltip>
        </Box>
        {owner && (
          <NavLink
            key={owner.getUserId()}
            to={`/user-profile/${owner.getUsername()}`}
            style={styles.contributorNavLink}
          >
            <Tooltip
              title={`Uploaded by ${owner.getUsername()}`}
              placement="top"
            >
              <Avatar
                style={styles.contributorsPhotos}
                src={
                  owner.getProfilePicture()
                    ? owner.getProfilePicture()
                    : "/assets/images/logo512.png"
                }
                alt={owner.getUsername()}
              />
            </Tooltip>
          </NavLink>
        )}

        <LabelDateBox>
          <Tooltip title={label} placement="top">
            <LabelText noWrap>{label}</LabelText>
          </Tooltip>
          <DateText noWrap>{`${mm} ${dd} ${year}`}</DateText>
        </LabelDateBox>
        <TimeBox>{`${hours > 12 ? hours % 12 : hours}:${minutes}:${seconds} ${
          hours > 12 ? "PM" : "AM"
        }`}</TimeBox>
        <BpmBox>BPM: {bpm !== 0 ? bpm : "---"}</BpmBox>
        <Box sx={styles.iconButtonsContainer}>
          <IconButton
            aria-label="play"
            size="small"
            onClick={() => handlePlayPause(id)}
          >
            {isPlaying ? (
              <PauseIcon sx={styles.playbackIcons} />
            ) : (
              <PlayArrowIcon sx={styles.playbackIcons} />
            )}
          </IconButton>

          <IconButton
            aria-label="stop"
            size="small"
            onClick={() => stopAllSongs()}
          >
            <StopIcon sx={styles.playbackIcons} />
          </IconButton>
          <IconButton
            sx={{ marginLeft: "-5px", paddingTop: "11px", color: "white" }}
            aria-label="download"
            onClick={downloadSong}
          >
            <DownloadIcon />
          </IconButton>

          <LightTooltip
            enterTouchDelay={0}
            enterDelay={300}
            title={
              isSelected
                ? "Cannot delete the current version"
                : userId !== ownerId
                ? "Only owner can delete the version"
                : ""
            }
            placement="top"
          >
            {/* using span so that tooltip shows on disabled button */}
            <span>
              <IconButton
                sx={{ marginLeft: "-10px", paddingTop: "14px", color: "white" }}
                onClick={() => handleOpenDeleteModal(id)}
                aria-label="delete"
                disabled={userId !== ownerId || isSelected}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </LightTooltip>
        </Box>
      </VersionDetailsBox>
    </>
  );
};
